import {
  Building2,
  CalendarDays,
  Clock3,
  DollarSign,
  Filter,
  Layers3,
  ReceiptText,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  WalletCards
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from "react";
import type {
  ProfitLossBreakdownRow,
  ProfitLossMetricValue,
  ProfitLossSankeyLink,
  ProfitLossSankeyNode
} from "../../shared/types";
import { KineticTextLoader } from "../components/common/KineticTextLoader";
import { StateBlock } from "../components/common/StateBlock";
import {
  getProfitLoss,
  readCachedProfitLoss,
  type ProfitLossQueryFilters,
  type ProfitLossSnapshotView
} from "../services/profitLossService";
import "../styles/profitLoss.css";

interface LayoutNode extends ProfitLossSankeyNode {
  height: number;
  width: number;
  x: number;
  y: number;
}

interface LayoutLink extends ProfitLossSankeyLink {
  path: string;
  thickness: number;
}

interface SankeyLayout {
  height: number;
  links: LayoutLink[];
  nodes: LayoutNode[];
  width: number;
}

interface DragState {
  nodeId: string;
  offsetX: number;
  offsetY: number;
  pointerId: number;
}

type NodePositionOverrides = Record<string, { x: number; y: number }>;
type RoutableLink = ProfitLossSankeyLink & { index?: number; thickness: number };

const chartWidth = 1220;
const nodeWidth = 18;
const chartMarginX = 164;
const chartMarginY = 46;
const compactCurrencyFormatterCache = new Map<string, Intl.NumberFormat>();
const fullCurrencyFormatterCache = new Map<string, Intl.NumberFormat>();
const percentFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, style: "percent" });
const numberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function getCurrencyFormatter(currency: string, compact = false): Intl.NumberFormat {
  const cache = compact ? compactCurrencyFormatterCache : fullCurrencyFormatterCache;
  const key = `${currency}-${compact ? "compact" : "full"}`;
  const cached = cache.get(key);
  if (cached) {
    return cached;
  }

  const formatter = new Intl.NumberFormat(undefined, {
    currency,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: compact ? 1 : 0,
    notation: compact ? "compact" : "standard",
    style: "currency"
  });
  cache.set(key, formatter);
  return formatter;
}

function formatCurrency(value: number, currency: string, compact = true): string {
  return getCurrencyFormatter(currency, compact).format(value);
}

function formatMetricValue(metric: ProfitLossMetricValue, currency: string): string {
  if (metric.unit === "currency") {
    return formatCurrency(metric.value, currency);
  }
  if (metric.unit === "percent") {
    return percentFormatter.format(metric.value / 100);
  }
  return numberFormatter.format(metric.value);
}

function formatDelta(value?: number): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}% Y/Y`;
}

function groupLabel(group: ProfitLossBreakdownRow["group"]): string {
  if (group === "revenue") {
    return "Revenue";
  }
  if (group === "cost") {
    return "Cost of revenue";
  }
  if (group === "expense") {
    return "Expense";
  }
  if (group === "tax") {
    return "Tax";
  }
  return "Profit";
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function routeSankeyLinks(layoutNodes: LayoutNode[], links: RoutableLink[]): LayoutLink[] {
  const nodeById = new Map(layoutNodes.map((node) => [node.id, node]));
  const linkMeta = links.map((link, index) => ({
    ...link,
    index: link.index ?? index
  }));
  const linkSourceY = new Map<number, number>();
  const linkTargetY = new Map<number, number>();

  layoutNodes.forEach((node) => {
    const sourceLinks = linkMeta
      .filter((link) => link.source === node.id)
      .sort((left, right) => (nodeById.get(left.target)?.y || 0) - (nodeById.get(right.target)?.y || 0));
    const targetLinks = linkMeta
      .filter((link) => link.target === node.id)
      .sort((left, right) => (nodeById.get(left.source)?.y || 0) - (nodeById.get(right.source)?.y || 0));

    let sourceCursor = node.y + ((node.height - sourceLinks.reduce((total, link) => total + link.thickness, 0)) / 2);
    sourceLinks.forEach((link) => {
      linkSourceY.set(link.index, sourceCursor + (link.thickness / 2));
      sourceCursor += link.thickness;
    });

    let targetCursor = node.y + ((node.height - targetLinks.reduce((total, link) => total + link.thickness, 0)) / 2);
    targetLinks.forEach((link) => {
      linkTargetY.set(link.index, targetCursor + (link.thickness / 2));
      targetCursor += link.thickness;
    });
  });

  return linkMeta
    .map((link): LayoutLink | undefined => {
      const source = nodeById.get(link.source);
      const target = nodeById.get(link.target);
      if (!source || !target) {
        return undefined;
      }

      const sourceX = source.x + source.width;
      const targetX = target.x;
      const sourceY = linkSourceY.get(link.index) ?? source.y + (source.height / 2);
      const targetY = linkTargetY.get(link.index) ?? target.y + (target.height / 2);
      const dx = targetX - sourceX;
      const path = [
        `M ${sourceX} ${sourceY}`,
        `C ${sourceX + (dx * 0.48)} ${sourceY}`,
        `${targetX - (dx * 0.48)} ${targetY}`,
        `${targetX} ${targetY}`
      ].join(" ");

      return {
        label: link.label,
        path,
        source: link.source,
        target: link.target,
        thickness: link.thickness,
        tone: link.tone,
        value: link.value
      };
    })
    .filter((link): link is LayoutLink => Boolean(link));
}

function buildSankeyLayout(nodes: ProfitLossSankeyNode[], links: ProfitLossSankeyLink[]): SankeyLayout {
  const levels = [...new Set(nodes.map((node) => node.level))].sort((left, right) => left - right);
  const maxLevelCount = Math.max(1, ...levels.map((level) => nodes.filter((node) => node.level === level).length));
  const chartHeight = clamp(160 + (maxLevelCount * 58), 580, 940);
  const maxValue = Math.max(1, ...nodes.map((node) => node.value), ...links.map((link) => link.value));
  const linkThickness = (value: number) => clamp((value / maxValue) * 92, 4, 84);
  const linkMeta = links.map((link, index) => ({
    ...link,
    index,
    thickness: linkThickness(link.value)
  }));
  const xForLevel = (level: number) => {
    const levelIndex = Math.max(0, levels.indexOf(level));
    const step = levels.length > 1 ? (chartWidth - (chartMarginX * 2) - nodeWidth) / (levels.length - 1) : 0;
    return chartMarginX + (levelIndex * step);
  };
  const levelGroups = levels.map((level) => nodes
    .filter((node) => node.level === level)
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0) || left.label.localeCompare(right.label)));
  const incoming = new Map<string, number>();
  const outgoing = new Map<string, number>();

  linkMeta.forEach((link) => {
    incoming.set(link.target, (incoming.get(link.target) || 0) + link.thickness);
    outgoing.set(link.source, (outgoing.get(link.source) || 0) + link.thickness);
  });

  const layoutNodes: LayoutNode[] = [];
  levelGroups.forEach((group) => {
    const sized = group.map((node) => ({
      ...node,
      height: clamp(Math.max(
        (node.value / maxValue) * 132,
        incoming.get(node.id) || 0,
        outgoing.get(node.id) || 0,
        24
      ), 24, 132),
      width: nodeWidth,
      x: xForLevel(node.level),
      y: 0
    }));
    const availableHeight = chartHeight - (chartMarginY * 2);
    const totalHeight = sized.reduce((total, node) => total + node.height, 0);
    const naturalGap = sized.length > 1 ? (availableHeight - totalHeight) / (sized.length - 1) : 0;
    const gap = sized.length > 1 ? clamp(naturalGap, 14, 34) : 0;
    const totalBlockHeight = totalHeight + (gap * Math.max(0, sized.length - 1));
    let y = Math.max(chartMarginY, (chartHeight - totalBlockHeight) / 2);

    sized.forEach((node) => {
      layoutNodes.push({ ...node, y });
      y += node.height + gap;
    });
  });

  return {
    height: chartHeight,
    links: routeSankeyLinks(layoutNodes, linkMeta),
    nodes: layoutNodes,
    width: chartWidth
  };
}

function applyNodeOverrides(layout: SankeyLayout, overrides: NodePositionOverrides): SankeyLayout {
  if (!Object.keys(overrides).length) {
    return layout;
  }

  const nodes = layout.nodes.map((node) => {
    const override = overrides[node.id];
    if (!override) {
      return node;
    }

    return {
      ...node,
      x: clamp(override.x, 8, layout.width - node.width - 8),
      y: clamp(override.y, 8, layout.height - node.height - 8)
    };
  });

  return {
    ...layout,
    links: routeSankeyLinks(nodes, layout.links),
    nodes
  };
}

function svgPointFromPointer(svg: SVGSVGElement, event: PointerEvent<SVGGElement>): { x: number; y: number } | undefined {
  const transform = svg.getScreenCTM();
  if (!transform) {
    return undefined;
  }

  const point = svg.createSVGPoint();
  point.x = event.clientX;
  point.y = event.clientY;
  return point.matrixTransform(transform.inverse());
}

function labelPosition(node: LayoutNode): { anchor: "middle" | "start" | "end"; height: number; width: number; x: number; y: number } {
  if (node.level === 0) {
    return { anchor: "end", height: 58, width: 142, x: node.x - 154, y: node.y + (node.height / 2) - 28 };
  }
  if (node.level >= 3) {
    return { anchor: "start", height: 62, width: 168, x: node.x + node.width + 12, y: node.y + (node.height / 2) - 30 };
  }

  return { anchor: "middle", height: 62, width: 150, x: node.x - 66, y: node.y + (node.height / 2) - 30 };
}

function ProfitLossKpiTile({ icon, metric, tone, currency }: { currency: string; icon: ReactNode; metric: ProfitLossMetricValue; tone: "revenue" | "profit" | "expense" | "neutral" }) {
  const delta = metric.deltaLabel || formatDelta(metric.delta);

  return (
    <section className={`profit-loss-kpi profit-loss-kpi--${tone}`}>
      <span>
        {icon}
        {metric.label}
      </span>
      <strong>{formatMetricValue(metric, currency)}</strong>
      <small>{delta || metric.helper || "EDW_ProfitNLoss"}</small>
    </section>
  );
}

function ProfitLossSankeyChart({ currency, snapshot }: { currency: string; snapshot: ProfitLossSnapshotView }) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [dragState, setDragState] = useState<DragState | undefined>();
  const [nodeOverrides, setNodeOverrides] = useState<NodePositionOverrides>({});
  const baseLayout = useMemo(() => buildSankeyLayout(snapshot.sankey.nodes, snapshot.sankey.links), [snapshot]);
  const layout = useMemo(() => applyNodeOverrides(baseLayout, nodeOverrides), [baseLayout, nodeOverrides]);
  const nodeById = useMemo(() => new Map(layout.nodes.map((node) => [node.id, node])), [layout]);

  useEffect(() => {
    setDragState(undefined);
    setNodeOverrides({});
  }, [snapshot]);

  function handleNodePointerDown(event: PointerEvent<SVGGElement>, node: LayoutNode): void {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }

    const point = svgPointFromPointer(svg, event);
    if (!point) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragState({
      nodeId: node.id,
      offsetX: point.x - node.x,
      offsetY: point.y - node.y,
      pointerId: event.pointerId
    });
  }

  function handleNodePointerMove(event: PointerEvent<SVGGElement>): void {
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const svg = svgRef.current;
    const node = nodeById.get(dragState.nodeId);
    if (!svg || !node) {
      return;
    }

    const point = svgPointFromPointer(svg, event);
    if (!point) {
      return;
    }

    event.preventDefault();
    setNodeOverrides((current) => ({
      ...current,
      [dragState.nodeId]: {
        x: clamp(point.x - dragState.offsetX, 8, layout.width - node.width - 8),
        y: clamp(point.y - dragState.offsetY, 8, layout.height - node.height - 8)
      }
    }));
  }

  function handleNodePointerEnd(event: PointerEvent<SVGGElement>): void {
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDragState(undefined);
  }

  if (!snapshot.sankey.nodes.length) {
    return <StateBlock type="empty" title="No Sankey flow" message="The selected Profit & Loss filters did not return statement lines." />;
  }

  return (
    <div className="profit-loss-sankey-scroll">
      <svg
        aria-label="Profit and loss Sankey chart"
        className="profit-loss-sankey"
        ref={svgRef}
        role="img"
        viewBox={`0 0 ${layout.width} ${layout.height}`}
      >
        <g className="profit-loss-sankey__links">
          {layout.links.map((link, index) => {
            const source = nodeById.get(link.source);
            const target = nodeById.get(link.target);
            return (
              <path
                className={`profit-loss-sankey-link profit-loss-sankey-link--${link.tone}`}
                d={link.path}
                key={`${link.source}-${link.target}-${index}`}
                strokeWidth={link.thickness}
              >
                <title>
                  {source?.label || link.source} to {target?.label || link.target}: {formatCurrency(link.value, currency, false)}
                </title>
              </path>
            );
          })}
        </g>

        <g className="profit-loss-sankey__nodes">
          {layout.nodes.map((node) => {
            const position = labelPosition(node);
            const delta = formatDelta(node.delta);
            const isDragging = dragState?.nodeId === node.id;
            return (
              <g
                className={`profit-loss-sankey-draggable-node${isDragging ? " is-dragging" : ""}`}
                key={node.id}
                onPointerCancel={handleNodePointerEnd}
                onPointerDown={(event) => handleNodePointerDown(event, node)}
                onPointerMove={handleNodePointerMove}
                onPointerUp={handleNodePointerEnd}
              >
                <rect
                  className={`profit-loss-sankey-node profit-loss-sankey-node--${node.tone}`}
                  height={node.height}
                  rx="5"
                  width={node.width}
                  x={node.x}
                  y={node.y}
                >
                  <title>
                    {node.label}: {formatCurrency(node.value, currency, false)}
                    {delta ? `, ${delta}` : ""}
                  </title>
                </rect>
                <foreignObject height={position.height} width={position.width} x={position.x} y={position.y}>
                  <div className={`profit-loss-sankey-label profit-loss-sankey-label--${position.anchor}`}>
                    <span>{node.label}</span>
                    <strong>{formatCurrency(node.value, currency)}</strong>
                    {delta && <small>{delta}</small>}
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

function InsightStrip({ snapshot }: { snapshot: ProfitLossSnapshotView }) {
  const revenue = snapshot.overview.revenue.value || 1;
  const topRevenueRow = [...snapshot.breakdown].filter((row) => row.group === "revenue").sort((left, right) => right.value - left.value)[0];
  const topExpenseRow = [...snapshot.breakdown].filter((row) => row.group === "cost" || row.group === "expense").sort((left, right) => right.value - left.value)[0];
  const grossMargin = snapshot.overview.grossProfit.value / revenue;
  const netMargin = snapshot.overview.netProfit.value / revenue;
  const expenseRatio = snapshot.overview.operatingExpenses.value / revenue;

  const insights = [
    {
      helper: topRevenueRow ? `${formatCurrency(topRevenueRow.value, snapshot.currency)} / ${percentFormatter.format(topRevenueRow.value / revenue)}` : "Waiting for source breakdown",
      label: "Top revenue line",
      value: topRevenueRow?.label.split(" / ").slice(-1)[0] || "Revenue"
    },
    {
      helper: topExpenseRow ? `${formatCurrency(topExpenseRow.value, snapshot.currency)} / ${percentFormatter.format(topExpenseRow.value / revenue)}` : "Waiting for expense breakdown",
      label: "Largest outflow",
      value: topExpenseRow?.label.split(" / ").slice(-1)[0] || "Expense"
    },
    {
      helper: `${formatCurrency(snapshot.overview.grossProfit.value, snapshot.currency)} after cost of revenue`,
      label: "Gross margin",
      value: percentFormatter.format(grossMargin)
    },
    {
      helper: `${formatCurrency(snapshot.overview.operatingExpenses.value, snapshot.currency)} operating expenses`,
      label: "Expense ratio",
      value: percentFormatter.format(expenseRatio)
    },
    {
      helper: `${formatCurrency(snapshot.overview.netProfit.value, snapshot.currency)} net income`,
      label: "Net margin",
      value: percentFormatter.format(netMargin)
    }
  ];

  return (
    <div className="profit-loss-insight-grid">
      {insights.map((insight) => (
        <section className="profit-loss-insight" key={insight.label}>
          <span>{insight.label}</span>
          <strong>{insight.value}</strong>
          <small>{insight.helper}</small>
        </section>
      ))}
    </div>
  );
}

function BreakdownTable({ currency, rows }: { currency: string; rows: ProfitLossBreakdownRow[] }) {
  return (
    <div className="table-scroll">
      <table className="data-table compact profit-loss-table">
        <thead>
          <tr>
            <th>Statement line</th>
            <th>Category</th>
            <th>Amount</th>
            <th>% Revenue</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.group}-${row.label}`}>
              <td><strong>{row.label}</strong></td>
              <td><span className={`profit-loss-group-pill profit-loss-group-pill--${row.group}`}>{groupLabel(row.group)}</span></td>
              <td>{formatCurrency(row.value, currency, false)}</td>
              <td>{row.percentOfRevenue.toLocaleString(undefined, { maximumFractionDigits: 1 })}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FilterField({ children, icon, label }: { children: ReactNode; icon: ReactNode; label: string }) {
  return (
    <label className="profit-loss-filter-field">
      <span>
        {icon}
        {label}
      </span>
      {children}
    </label>
  );
}

function filtersFromSnapshot(snapshot?: ProfitLossSnapshotView): ProfitLossQueryFilters {
  const selected = snapshot?.filters?.selected;
  return {
    ...(selected?.month ? { month: selected.month } : {}),
    ...(selected?.station ? { station: selected.station } : {}),
    ...(selected?.year ? { year: selected.year } : {})
  };
}

export function ProfitLossPage() {
  const [filters, setFilters] = useState<ProfitLossQueryFilters>({});
  const [snapshot, setSnapshot] = useState<ProfitLossSnapshotView | undefined>(() => readCachedProfitLoss());
  const [isLoading, setIsLoading] = useState(!snapshot);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(undefined);

    getProfitLoss({ filters })
      .then((nextSnapshot) => {
        if (isMounted) {
          setSnapshot(nextSnapshot);
        }
      })
      .catch((err: Error) => {
        if (isMounted) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [filters]);

  const availableFilters = snapshot?.filters?.available;
  const selectedFilters = {
    ...filtersFromSnapshot(snapshot),
    ...filters
  };
  const selectedStation = selectedFilters.station || "all";
  const selectedYear = selectedFilters.year ? String(selectedFilters.year) : "";
  const selectedMonth = selectedFilters.month ? String(selectedFilters.month) : "";
  const selectedStationLabel = snapshot?.filters?.selected.stationLabel || "All stations";

  function updateFilters(nextFilters: ProfitLossQueryFilters): void {
    setFilters((current) => ({
      ...current,
      ...nextFilters
    }));
  }

  function selectStation(station: string): void {
    updateFilters({
      station: station === "all" ? undefined : station
    });
  }

  function selectYear(value: string): void {
    updateFilters({
      month: undefined,
      year: value ? Number(value) : undefined
    });
  }

  function selectMonth(value: string): void {
    updateFilters({
      month: value ? Number(value) : undefined
    });
  }

  function refreshSnapshot(): void {
    setIsLoading(true);
    setError(undefined);
    getProfitLoss({ filters, forceRefresh: true })
      .then(setSnapshot)
      .catch((err: Error) => setError(err.message))
      .finally(() => setIsLoading(false));
  }

  if (isLoading && !snapshot) {
    return <StateBlock type="loading" title="Loading Profit & Loss" message="Querying EDW_ProfitNLoss and shaping station-level statement flow." />;
  }

  if (error || !snapshot) {
    return <StateBlock type="error" title="Profit & Loss unavailable" message={error || "Profit & Loss data is missing."} />;
  }

  return (
    <section className="page-stack profit-loss-page">
      <div className="page-heading profit-loss-page-heading">
        <div>
          <p>Financial reports</p>
          <h2>Profit & Loss</h2>
        </div>
        <div className="page-heading-actions">
          <span className="timestamp-pill">
            <Clock3 size={15} aria-hidden="true" />
            {snapshot.periodLabel}
          </span>
          <span className="data-source-pill source-powerbi">
            <Layers3 size={15} aria-hidden="true" />
            EDW_ProfitNLoss
          </span>
        </div>
      </div>

      <div className="profit-loss-kpi-grid">
        <ProfitLossKpiTile currency={snapshot.currency} icon={<DollarSign size={17} aria-hidden="true" />} metric={snapshot.overview.revenue} tone="revenue" />
        <ProfitLossKpiTile currency={snapshot.currency} icon={<TrendingUp size={17} aria-hidden="true" />} metric={snapshot.overview.grossProfit} tone="profit" />
        <ProfitLossKpiTile currency={snapshot.currency} icon={<WalletCards size={17} aria-hidden="true" />} metric={snapshot.overview.operatingProfit} tone="profit" />
        <ProfitLossKpiTile currency={snapshot.currency} icon={<ReceiptText size={17} aria-hidden="true" />} metric={snapshot.overview.operatingExpenses} tone="expense" />
        <ProfitLossKpiTile currency={snapshot.currency} icon={snapshot.overview.netProfit.value >= 0 ? <TrendingUp size={17} aria-hidden="true" /> : <TrendingDown size={17} aria-hidden="true" />} metric={snapshot.overview.netProfit} tone="profit" />
      </div>

      <InsightStrip snapshot={snapshot} />

      <section className="panel profit-loss-filter-panel">
        <div className="panel-heading">
          <div>
            <p>Station and period filters</p>
            <h3>{selectedStationLabel}</h3>
          </div>
          <button className="icon-action-button" disabled={isLoading} onClick={refreshSnapshot} title="Refresh Profit & Loss">
            <RefreshCw size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="profit-loss-filter-grid">
          <FilterField icon={<Building2 size={16} aria-hidden="true" />} label="Station">
            <select value={selectedStation} onChange={(event) => selectStation(event.target.value)}>
              <option value="all">All stations</option>
              {(availableFilters?.stations || []).map((station) => (
                <option key={station.value} value={station.value}>{station.shortName || station.label}</option>
              ))}
            </select>
          </FilterField>

          <FilterField icon={<CalendarDays size={16} aria-hidden="true" />} label="Year">
            <select value={selectedYear} onChange={(event) => selectYear(event.target.value)}>
              {(availableFilters?.years || []).map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </FilterField>

          <FilterField icon={<Filter size={16} aria-hidden="true" />} label="Month">
            <select value={selectedMonth} onChange={(event) => selectMonth(event.target.value)}>
              <option value="">YTD</option>
              {(availableFilters?.months || []).map((month) => (
                <option key={month} value={month}>{monthNames[month - 1] || `Month ${month}`}</option>
              ))}
            </select>
          </FilterField>
        </div>

        <div className="profit-loss-station-rail" aria-label="Station shortcuts">
          <button className={selectedStation === "all" ? "active" : ""} onClick={() => selectStation("all")} type="button">All</button>
          {(availableFilters?.stations || []).map((station) => (
            <button
              className={selectedStation === station.value ? "active" : ""}
              key={station.value}
              onClick={() => selectStation(station.value)}
              type="button"
            >
              {station.shortName || station.label}
            </button>
          ))}
        </div>
      </section>

      <section className="panel profit-loss-sankey-panel">
        <div className="panel-heading">
          <div>
            <p>Station Sankey</p>
            <h3>Revenue source to Net Income</h3>
          </div>
          <span className="profit-loss-chart-source">{snapshot.periodLabel}</span>
        </div>
        {isLoading && <div aria-live="polite" className="profit-loss-refreshing"><KineticTextLoader className="kinetic-text-loader--compact" text="Refreshing" /></div>}
        <ProfitLossSankeyChart currency={snapshot.currency} snapshot={snapshot} />
      </section>

      <section className="panel profit-loss-detail-panel">
        <div className="panel-heading">
          <div>
            <p>Statement lines</p>
            <h3>Domain / Subdomain / Detail</h3>
          </div>
          <span className="profit-loss-chart-source">{snapshot.asOfDate}</span>
        </div>
        <BreakdownTable currency={snapshot.currency} rows={snapshot.breakdown} />
      </section>
    </section>
  );
}
